/**
 * Progress Data Transformer
 * 
 * Transforms backend progress data format to match frontend UI structure.
 */

// Service name to display name mapping
export const SERVICE_DISPLAY_NAMES = {
  'tiling': 'File Tiling',
  'csf': 'Ground Classification',
  'car_removal': 'Vehicle Removal',
  'road_detection': 'Road Detection',
  'create_rsa': 'RSA Analysis',
  'file_transfer': 'File Transfer',
  'embankment': 'Embankment Analysis',
  'horizontal': 'Horizontal Curves',
  'slope_calc': 'Slope Calculation',
  'psd_classification': 'PSD Classification',
  'automatic_report': 'Report Generation',
  'report_generation': 'Report Generation',
  'automatic_reports': 'Auto Report'
};

// Service execution order (for step numbering)
export const SERVICE_ORDER = [
  'tiling',
  'csf',
  'car_removal',
  'road_detection',
  'horizontal',
  'slope_calc',
  'psd_classification',
  'embankment',
  'automatic_report',
  'report_generation'
];

/**
 * Transform backend progress data to frontend UI format
 * @param {Object} backendData - Progress data from backend
 * @param {Object} taskMetadata - Additional task metadata (project_id, run_id, etc.)
 * @returns {Object} Transformed data matching UI structure
 */
export const transformProgressData = (backendData, taskMetadata = {}) => {
  const { services = {}, overall_progress = 0, completed_services = 0, total_services = 0 } = backendData;
  
  // Get all service names and sort by execution order
  const serviceNames = Object.keys(services).sort((a, b) => {
    const indexA = SERVICE_ORDER.indexOf(a);
    const indexB = SERVICE_ORDER.indexOf(b);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  // Transform services to steps array
  const steps = serviceNames.map((serviceName, index) => {
    const serviceData = services[serviceName] || {};
    const progress = serviceData.progress || 0;
    const status = serviceData.status || (progress >= 100 ? 'completed' : progress > 0 ? 'running' : 'pending');
    
    return {
      name: serviceName,
      display_name: SERVICE_DISPLAY_NAMES[serviceName] || serviceName,
      status: status,
      progress: Math.min(100, Math.max(0, progress)),
      message: serviceData.message || '',
      started_at: serviceData.timestamp ? new Date(serviceData.timestamp * 1000) : null,
      completed_at: serviceData.completed_at ? new Date(serviceData.completed_at) : (progress >= 100 && serviceData.timestamp ? new Date(serviceData.timestamp * 1000) : null),
      duration: serviceData.duration || null,
      description: getServiceDescription(serviceName),
      instance_id: serviceData.instance_id || null,
      folder_id: serviceData.folder_id || null,
      folder_name: serviceData.folder_name || null
    };
  });

  // Determine overall status
  let overallStatus = 'pending';
  if (completed_services === total_services && total_services > 0) {
    overallStatus = 'completed';
  } else if (overall_progress > 0) {
    overallStatus = 'running';
  }

  // Calculate completed steps count
  const completedSteps = steps.filter(step => step.status === 'completed').length;

  return {
    id: taskMetadata.run_id || taskMetadata.task_id || 'unknown',
    project_id: taskMetadata.project_id || null,
    project_name: taskMetadata.project_name || `Project ${taskMetadata.project_id || 'Unknown'}`,
    run_id: taskMetadata.run_id || taskMetadata.task_id || null,
    status: overallStatus,
    start_time: taskMetadata.created_at ? new Date(taskMetadata.created_at) : new Date(),
    completed_at: overallStatus === 'completed' ? new Date() : null,
    progress: {
      percentage: Math.round(overall_progress),
      completed_steps: completedSteps,
      total_steps: steps.length || total_services
    },
    steps: steps,
    input_files: taskMetadata.input_files || [],
    user_id: taskMetadata.user_email || null,
    // Keep original backend data for reference
    _raw: backendData
  };
};

/**
 * Get service description
 * @param {string} serviceName - Service name
 * @returns {string} Description
 */
const getServiceDescription = (serviceName) => {
  const descriptions = {
    'tiling': 'Breaking large LiDAR files into manageable tiles for processing',
    'csf': 'Classifying ground points using CSF algorithm',
    'car_removal': 'Removing vehicles and other moving objects from the point cloud',
    'road_detection': 'Detecting road boundaries',
    'embankment': 'Analyzing road embankments',
    'horizontal': 'Calculating horizontal curves and alignments',
    'slope_calc': 'Calculating slope values and gradients',
    'psd_classification': 'Classifying PSD (Particle Size Distribution) data',
    'automatic_report': 'Generating automatic analysis reports',
    'report_generation': 'Generating final analysis report with visualizations'
  };
  return descriptions[serviceName] || `Processing ${serviceName}`;
};

/**
 * Transform task list item for projects-list
 * @param {Object} task - Task object from backend
 * @returns {Object} Transformed task for list display
 */
export const transformTaskForList = (task) => {
  const {
    run_id,
    full_run_id,
    task_id,
    project_id,
    project_name,  // Add project_name
    user_email,
    folder_id,
    services = [],
    services_progress = {},
    overall_progress = 0,
    status = 'pending',
    created_at,
    updated_at,
    source
  } = task;

  // Count services by status
  const serviceEntries = Object.entries(services_progress);
  const completedServices = serviceEntries.filter(([_, data]) => 
    (data.progress || 0) >= 100 || data.status === 'completed'
  ).length;
  const runningServices = serviceEntries.filter(([_, data]) => 
    (data.progress || 0) > 0 && (data.progress || 0) < 100 && data.status === 'processing'
  ).length;

  // Generate a more descriptive name
  const runDisplayName = project_id 
    ? `Project ${project_id} - Run ${run_id || task_id || 'Unknown'}`
    : `Run ${run_id || task_id || 'Unknown'}`;
  
  return {
    id: run_id || full_run_id || task_id,
    name: project_name || runDisplayName,  // Use project_name if available, otherwise use generated name
    description: project_id 
      ? `Pipeline run for project ${project_id}`
      : `Pipeline run ${run_id || task_id || 'Unknown'}`,
    project_id: project_id,
    project_name: project_name,  // Include project_name in returned object
    run_id: run_id || full_run_id || task_id,
    total_analyses: 1, // Each run is one analysis
    completed_analyses: status === 'completed' ? 1 : 0,
    running_analyses: status === 'processing' || status === 'running' ? 1 : 0,
    last_updated: updated_at ? new Date(updated_at) : (created_at ? new Date(created_at) : new Date()),
    overall_progress: overall_progress,
    status: status,
    services: services,
    services_progress: services_progress,
    created_at: created_at,
    user_email: user_email,
    folder_id: folder_id,
    source: source // 'redis' or 'mongodb'
  };
};

